import { FC } from "preact/compat";
import { useComputed } from "@preact/signals";
import { Link } from "react-router-dom";
import { ExternalTile, TrackGroupTile } from "~/core";
import { me, useApp } from "~/core/store";
import { IconButton, TimeDistance } from "~/components";
import { useDialogUrlState } from "~/hooks";
import { SwipePanel } from "../SwipePanel";
import Tile from "./Tile";
import PauseIcon from "@material-design-icons/svg/outlined/pause.svg";
import ScheduleIcon from "@material-design-icons/svg/outlined/schedule.svg";
import Loader from "~/assets/loader.svg";

const EACH_SECOND = 1000;

const TrackerPageMobile: FC = () => {
    const {
        mobile: { toggleTrackerPage },
        tracker: {
            loading,
            activeTask,
            activeTile,
            activeExternal,
            tiles,
            stop,
        },
    } = useApp();
    const { mobile } = useApp();
    const visibleTiles = useComputed(() => {
        const result: (ExternalTile | TrackGroupTile)[] = tiles.value.filter(
            (tile) => !tile.inactive,
        );
        const ext = activeExternal.value;

        if (ext) {
            result.unshift(ext);
        }

        return result;
    });

    useDialogUrlState(mobile.isTrackerPage, { queryParam: "tracker" });

    const active = activeTask.value ?? activeTile.value ?? activeExternal.value;

    const handleStop = () => {
        if (active?._track) {
            stop(active._track);
        }
    };

    return (
        <SwipePanel
            disabled
            to="left"
            zIndex={100}
            active={mobile.isTrackerPage}
            onSwipe={() => mobile.toggleTrackerPage(true)}
            onSwipeBack={() => mobile.toggleTrackerPage(false)}
            containerClassName="bg-white/80 backdrop-blur"
        >
            <div className="sticky top-0 mb-3 flex items-center border-b border-gray-200 bg-white px-6 py-3">
                <IconButton disabled={!active} onClick={handleStop}>
                    {loading.value ? (
                        <Loader width="18" height="18" />
                    ) : active ? (
                        <PauseIcon width="18" height="18" />
                    ) : (
                        <ScheduleIcon width="18" height="18" />
                    )}
                </IconButton>
                {active?.startDate && (
                    <TimeDistance
                        className="ml-4"
                        date={active.startDate}
                        period={EACH_SECOND}
                        withSeconds
                    />
                )}
                <span className="ml-4 truncate">
                    {active?.name ??
                        (loading.value ? "Loading..." : "No active timer")}
                </span>
            </div>
            <div className="flex flex-col gap-y-2 px-6 pb-10">
                {visibleTiles.value.map((tile) => (
                    <Tile key={tile.id} tile={tile} />
                ))}
            </div>
            {!loading.value && visibleTiles.value.length === 0 && (
                <div className="mb-3 px-9 py-3 text-center text-gray-500">
                    <p>You don't have any timers yet.</p>
                    <p>
                        Go to your{" "}
                        <Link
                            to={`${me("profile")}?tab=timer`}
                            onClick={() => toggleTrackerPage(false)}
                            className="text-sky-500 underline"
                        >
                            profile
                        </Link>{" "}
                        to create one.
                    </p>
                </div>
            )}
        </SwipePanel>
    );
};

export default TrackerPageMobile;
